import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormHelperText, Grid, Divider, Stack, Skeleton, MenuItem } from '@mui/material';
import { styles } from './styles';
import { useNavigate } from 'react-router-dom';
import TextField from '../../../components/common/InputField/TextField';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useBulkNewDesignationActionMutation, useModifyDesignationActionMutation } from '../../../api/designations';
import DesignationFields from '../DesignationFields';
import { mapBulkApiErrorsToFields } from '../../../utils/map-bulk-api-errors';

const nameSchema = Yup.string()
    .max(255)
    .required('Name is required')
    .matches(
        /^[A-Za-z0-9&()\-\s]+$/,
        'Name can contain only letters, numbers, spaces, ampersand (&), parentheses, and hyphens',
    );

const statusSchema = Yup.string().max(255).required('Status is required');

const designationItemSchema = Yup.object().shape({
    name: nameSchema,
    status: statusSchema,
});

const DesignationDetailsTab = ({ isEditMode, designationDetails, isLoading }) => {
    const navigate = useNavigate();
    const { status = [], results = {} } = designationDetails || {};
    const { enqueueSnackbar } = useSnackbar();
    const [bulkNewDesignationAction, { isLoading: isBulkNewLoading }] = useBulkNewDesignationActionMutation();
    const [modifyDesignationAction, { isLoading: isModifyLoading }] = useModifyDesignationActionMutation();
    const [serverFieldErrors, setServerFieldErrors] = useState([]);

    const loading = isBulkNewLoading || isModifyLoading;

    const applyServerFieldErrors = (designations, apiErrors) => {
        const fieldErrors = mapBulkApiErrorsToFields(designations, apiErrors);
        setServerFieldErrors(fieldErrors);
    };

    const handleClearServerError = (index) => {
        setServerFieldErrors((prev) => {
            const updated = [...prev];
            updated[index] = {};
            return updated;
        });
    };

    const handleCreateDesignations = async (designations) => {
        try {
            const response = await bulkNewDesignationAction({
                designations: designations.map((designation) => ({
                    name: designation.name,
                    status: designation.status,
                })),
            }).unwrap();

            if (!response) return;

            const apiErrors = response?.errors || {};
            const hasFieldErrors = Object.keys(apiErrors).length > 0;

            if (hasFieldErrors) {
                applyServerFieldErrors(designations, apiErrors);
                if (response?.message) {
                    enqueueSnackbar(response.message, { variant: 'error' });
                }
                return;
            }

            if (response?.status === 'ERROR') {
                enqueueSnackbar(response?.message || 'Something went wrong!', { variant: 'error' });
                return;
            }

            setServerFieldErrors([]);
            enqueueSnackbar(
                response?.message ||
                    (designations.length === 1
                        ? 'Designation created successfully.'
                        : `${designations.length} designations created successfully.`),
                { variant: 'success' },
            );
            navigate('/settings/designations');
        } catch (err) {
            const apiErrors = err?.data?.errors;
            if (apiErrors && Object.keys(apiErrors).length > 0) {
                applyServerFieldErrors(designations, apiErrors);
                if (err?.data?.message) {
                    enqueueSnackbar(err.data.message, { variant: 'error' });
                }
                return;
            }
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const handleModifyDesignation = async (details) => {
        try {
            const bodyFormData = new FormData();
            for (const key in details) {
                bodyFormData.append(key, details[key]);
            }
            const response = await modifyDesignationAction(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response.message, { variant: 'success' });
            navigate('/settings/designations');
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const formik = useFormik({
        initialValues: isEditMode
            ? {
                  id: results?.id ?? '',
                  name: results?.name ?? '',
                  status: results?.status ?? 'Active',
              }
            : {
                  designations: [{ name: '', status: 'Active' }],
              },
        enableReinitialize: true,
        validationSchema: isEditMode
            ? Yup.object().shape({
                  name: nameSchema,
                  status: statusSchema,
              })
            : Yup.object().shape({
                  designations: Yup.array()
                      .min(1, 'At least one designation is required')
                      .max(10, 'Maximum 10 designations allowed at a time')
                      .of(designationItemSchema),
              }),
        onSubmit: async (values, helpers) => {
            try {
                if (isEditMode) {
                    await handleModifyDesignation({ ...values, name: values.name?.trim().replace(/\s+/g, ' ') });
                } else {
                    const designations = values.designations.map((designation) => ({
                        ...designation,
                        name: designation.name?.trim().replace(/\s+/g, ' '),
                    }));
                    setServerFieldErrors([]);
                    await handleCreateDesignations(designations);
                }
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });

    if (isLoading) {
        return (
            <Box sx={{ py: 4 }}>
                <Skeleton height={42} />
                <Skeleton />
                <Skeleton />
            </Box>
        );
    }

    return (
        <>
            <Grid container spacing={4} sx={{ py: 4 }}>
                <Grid item md={12} xs={12}>
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <Grid container columnSpacing={6} rowSpacing={3}>
                                {isEditMode ? (
                                    <>
                                        <Grid item xs={4}>
                                            <TextField
                                                error={Boolean(formik.touched.name && formik.errors.name)}
                                                fullWidth
                                                helperText={formik.touched.name && formik.errors.name}
                                                label="Name"
                                                name="name"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                value={formik.values.name}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                fullWidth
                                                error={Boolean(formik.touched.status && formik.errors.status)}
                                                label="Status"
                                                name={'status'}
                                                helperText={formik.touched.status && formik.errors.status}
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                select
                                                children={status.map((status) => (
                                                    <MenuItem key={status} value={status}>
                                                        {status}
                                                    </MenuItem>
                                                ))}
                                                value={formik.values.status}
                                            />
                                        </Grid>
                                    </>
                                ) : (
                                    <DesignationFields
                                        designations={formik.values.designations}
                                        statusOptions={status}
                                        touched={formik.touched.designations}
                                        errors={formik.errors.designations}
                                        serverFieldErrors={serverFieldErrors}
                                        setDesignations={(values) => {
                                            if (values.length !== formik.values.designations.length) {
                                                setServerFieldErrors([]);
                                            }
                                            formik.setFieldValue('designations', values);
                                        }}
                                        onBlur={formik.setFieldTouched}
                                        onClearServerError={handleClearServerError}
                                    />
                                )}
                                {formik.errors.submit && (
                                    <Grid item xs={12}>
                                        <FormHelperText error>{formik.errors.submit}</FormHelperText>
                                    </Grid>
                                )}
                                {isEditMode && (
                                    <Grid item xs={12} sx={styles.dividerWrapper}>
                                        <Divider flexItem orientation="horizontal" sx={styles.divider} />{' '}
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Stack direction="row" justifyContent="end">
                                        <LoadingButton
                                            loading={loading}
                                            color="primary"
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                        >
                                            {isEditMode ? 'Update' : 'Create'}
                                        </LoadingButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                </Grid>
            </Grid>
        </>
    );
};

export default DesignationDetailsTab;
